import type { HotelPublicContent } from '@bandido/shared/types/hotels/hotel-content';
import type {
  HotelListOptions,
  HotelResultCard,
  HotelSortPrice,
} from '@bandido/shared/types/hotels/hotel-search-results';
import { publishedHotelPoint, type HotelLocationPoint } from './hotelLocationPoint';
import type { HotelSummary } from './searchModel';

export interface HotelMapPoint extends HotelLocationPoint {
  id: string;
  name: string;
  price: HotelSortPrice | null;
  content: HotelPublicContent;
}

export function hotelMapPoints(
  cards: HotelResultCard[],
  hotels: HotelSummary[],
  content: Record<string, HotelPublicContent>,
  options: HotelListOptions,
): HotelMapPoint[] {
  const names = new Map(hotels.map((hotel) => [hotel.hotel_id, hotel.display_name]));
  const seen = new Set<string>();
  return cards.flatMap((card) => {
    const published = content[card.property_id];
    const point = publishedHotelPoint(published);
    if (!point || seen.has(card.property_id)) return [];
    seen.add(card.property_id);
    const price = card.sort_price;
    return [
      {
        ...point,
        id: card.property_id,
        name: published.display_name?.trim() || names.get(card.property_id) || card.property_id,
        content: published,
        // Preview minima are truncated and cannot supply a hotel-wide minimum.
        price:
          (options.sort === 'price_asc' || options.sort === 'price_desc') &&
          price &&
          price.price.currency === options.currency &&
          price.price_basis === options.price_basis
            ? price
            : null,
      },
    ];
  });
}

/** Connected overlapping marker rectangles in screen pixels; bounded by the accepted search catalogue. */
export function clusterHotelMapPoints(
  points: HotelMapPoint[],
  project: (point: HotelMapPoint) => { x: number; y: number } | null,
): HotelMapPoint[][] {
  const projected = points.map((point) => ({ point, pixel: project(point) })).filter((p) => p.pixel !== null);
  const parent = projected.map((_, index) => index);
  const root = (index: number): number => {
    let current = index;
    while (parent[current] !== current) current = parent[current];
    return current;
  };
  for (let i = 0; i < projected.length; i++) {
    for (let j = 0; j < i; j++) {
      const a = projected[i].pixel!,
        b = projected[j].pixel!;
      if (Math.abs(a.x - b.x) < 180 && Math.abs(a.y - b.y) < 56) parent[root(i)] = root(j);
    }
  }
  const groups = new Map<number, HotelMapPoint[]>();
  projected.forEach(({ point }, index) => {
    const key = root(index);
    groups.set(key, [...(groups.get(key) ?? []), point]);
  });
  return [...groups.values()];
}
